import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import UseCaseSelection from './TeamOnboarding/UseCaseSelection';
import TeamTypeSelection from './TeamOnboarding/TeamTypeSelection';
import RoleSelection from './TeamOnboarding/RoleSelection';
import TemplateSelection from './TeamOnboarding/TemplateSelection';
import PricingSelection from './TeamOnboarding/PricingSelection';
import PaymentForm from './TeamOnboarding/PaymentForm';
import OnboardingSuccess from './TeamOnboarding/OnboardingSuccess';

const STEPS = {
  USE_CASE: 'use_case',
  TEAM_TYPE: 'team_type',
  ROLE: 'role',
  TEMPLATES: 'templates',
  PRICING: 'pricing', 
  PAYMENT: 'payment',
  SUCCESS: 'success'
};

const TeamOnboardingFlow = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.USE_CASE);
  const [formData, setFormData] = useState({
    useCase: null,
    teamType: null,
    role: null,
    templates: [],
    plan: null,
    paymentMethod: null,
    organizationName: '',
    billingDetails: {}
  });

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const nextStep = () => {
    const stepOrder = Object.values(STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  };

  const prevStep = () => {
    const stepOrder = Object.values(STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  const getStepProgress = () => {
    const stepOrder = Object.values(STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    return ((currentIndex + 1) / stepOrder.length) * 100;
  };

  const handleComplete = (finalData) => {
    onComplete({ ...formData, ...finalData });
    setCurrentStep(STEPS.SUCCESS);
  };

  const renderStep = () => {
    const stepProps = {
      formData,
      updateFormData,
      onNext: nextStep,
      onPrev: prevStep,
      onComplete: handleComplete
    };

    switch (currentStep) {
      case STEPS.USE_CASE:
        return <UseCaseSelection {...stepProps} />;
      case STEPS.TEAM_TYPE:
        return <TeamTypeSelection {...stepProps} />;
      case STEPS.ROLE:
        return <RoleSelection {...stepProps} />;
      case STEPS.TEMPLATES:
        return <TemplateSelection {...stepProps} />;
      case STEPS.PRICING:
        return <PricingSelection {...stepProps} />;
      case STEPS.PAYMENT:
        return <PaymentForm {...stepProps} />;
      case STEPS.SUCCESS:
        return <OnboardingSuccess {...stepProps} onFinish={onClose} />;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black z-50 flex flex-col"
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="flex-1 flex flex-col"
        >
          {/* Header with Progress */}
          <div className="relative px-8 py-6">
            {/* X Button - Top Right */}
            <div className="absolute top-6 right-8 z-10">
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-white/70 hover:text-white" />
              </button>
            </div>

            {/* Back Button - Left Side */}
            {currentStep !== STEPS.USE_CASE && currentStep !== STEPS.SUCCESS && (
              <div className="absolute top-6 left-8 z-10">
                <button
                  onClick={prevStep}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <ArrowLeftIcon className="w-5 h-5 text-white/70 hover:text-white" />
                </button>
              </div>
            )}

            {/* Centered Title */}
            <div className="text-center">
              <h1 className="text-xl font-semibold text-white">
                Welcome to Klorah Teams
              </h1>
              <p className="text-sm text-white/60 mt-1">
                Let's set up your photography team workspace
              </p>
            </div>

            {/* Progress Bar */}
            {currentStep !== STEPS.SUCCESS && (
              <div className="mt-6 flex justify-center">
                <div className="w-full max-w-lg">
                  <div className="w-full bg-white/10 rounded-full h-1">
                    <motion.div
                      className="bg-gradient-to-r from-brand to-[#FF9D5C] h-1 rounded-full shadow-lg shadow-orange-500/30"
                      initial={{ width: 0 }}
                      animate={{ width: `${getStepProgress()}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Step Content */}
          <div className="flex-1 overflow-y-auto bg-black">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="min-h-full"
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TeamOnboardingFlow;